package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/** Mapea el tipo de notificación al área del panel donde se resuelve. "" = no navega. */
private fun destinoDeNotificacion(tipo: String, mensaje: String): String = when {
    tipo == "cancelacion" -> "cancelaciones"
    tipo == "rol" -> "Cuentas"
    // Ventas por transferencia/efectivo que requieren confirmación del admin → Config. Pagos.
    tipo == "venta" && (mensaje.contains("transferencia", true) || mensaje.contains("confirmar", true) || mensaje.contains("efectivo", true)) -> "config_pagos"
    tipo == "venta" -> "historial_pagos"
    tipo == "alerta" && mensaje.contains("cancel", true) -> "cancelaciones"
    tipo == "alerta" -> "historial_pagos"
    else -> ""
}

@Composable
fun BandejaNotificaciones(
    viewModel: NotificacionViewModel,
    miUid: String,
    onCerrar: () -> Unit,
    onIrA: (String) -> Unit = {}
) {
    val notificaciones by viewModel.notificaciones.collectAsState()
    val noLeidas by viewModel.noLeidas.collectAsState()

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(SmartVanColors.FondoPanel)
            .padding(16.dp)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                IconButton(onClick = onCerrar) {
                    Icon(Icons.Filled.ArrowBack, contentDescription = "Regresar", tint = SmartVanColors.AzulOscuro)
                }
                Text("Notificaciones", fontWeight = FontWeight.Black, fontSize = 20.sp, color = SmartVanColors.AzulOscuro)
                if (noLeidas > 0) {
                    Spacer(modifier = Modifier.width(8.dp))
                    Badge(containerColor = Color.Red) {
                        Text("$noLeidas", color = Color.White, fontSize = 11.sp)
                    }
                }
            }
            if (noLeidas > 0) {
                TextButton(onClick = { viewModel.marcarTodasComoLeidas(miUid) }) {
                    Text("Marcar todas como leídas", fontSize = 12.sp, color = SmartVanColors.AzulOscuro)
                }
            }
        }

        Spacer(modifier = Modifier.height(12.dp))

        if (notificaciones.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    Icon(Icons.Filled.Notifications, contentDescription = null, tint = Color.LightGray, modifier = Modifier.size(64.dp))
                    Spacer(modifier = Modifier.height(12.dp))
                    Text("No tienes notificaciones", color = Color.Gray, fontSize = 16.sp)
                }
            }
        } else {
            LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                items(notificaciones) { notif ->
                    NotificacionCard(
                        notificacion = notif,
                        onClick = {
                            if (!notif.leida) {
                                viewModel.marcarComoLeida(notif.id, miUid)
                            }
                            // Un click te lleva al área donde se resuelve (cancelaciones, pagos, etc.)
                            val destino = destinoDeNotificacion(notif.tipo, notif.mensaje)
                            if (destino.isNotBlank()) onIrA(destino)
                        }
                    )
                }
            }
        }
    }
}

@Composable
fun NotificacionCard(notificacion: Notificacion, onClick: () -> Unit) {
    val icono = when (notificacion.tipo) {
        "venta" -> Icons.Filled.ShoppingCart
        "rol" -> Icons.Filled.Person
        "asignacion" -> Icons.Filled.DirectionsBus
        "viaje_hoy" -> Icons.Filled.Today
        "alerta" -> Icons.Filled.Warning
        else -> Icons.Filled.Notifications
    }

    val colorIcono = when (notificacion.tipo) {
        "venta" -> SmartVanColors.Verde
        "rol" -> SmartVanColors.Azul
        "asignacion" -> SmartVanColors.Naranja
        "viaje_hoy" -> SmartVanColors.AzulOscuro
        "alerta" -> Color.Red
        else -> Color.Gray
    }

    Card(
        modifier = Modifier
            .fillMaxWidth()
            .clickable { onClick() },
        colors = CardDefaults.cardColors(
            containerColor = if (notificacion.leida) Color.White else Color(0xFFE3F2FD)
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (notificacion.leida) 1.dp else 3.dp),
        shape = RoundedCornerShape(10.dp)
    ) {
        Row(
            modifier = Modifier.padding(12.dp),
            verticalAlignment = Alignment.Top
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(CircleShape)
                    .background(colorIcono.copy(alpha = 0.1f)),
                contentAlignment = Alignment.Center
            ) {
                Icon(icono, contentDescription = null, tint = colorIcono, modifier = Modifier.size(22.dp))
            }

            Spacer(modifier = Modifier.width(12.dp))

            Column(modifier = Modifier.weight(1f)) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        notificacion.titulo,
                        fontWeight = if (notificacion.leida) FontWeight.Normal else FontWeight.Bold,
                        fontSize = 14.sp,
                        color = SmartVanColors.AzulOscuro
                    )
                    if (!notificacion.leida) {
                        Box(
                            modifier = Modifier
                                .size(8.dp)
                                .clip(CircleShape)
                                .background(SmartVanColors.Azul)
                        )
                    }
                }
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    notificacion.mensaje,
                    fontSize = 13.sp,
                    color = Color.Gray,
                    maxLines = 3
                )
                Spacer(modifier = Modifier.height(4.dp))
                Text(
                    formatearTiempo(notificacion.timestamp),
                    fontSize = 11.sp,
                    color = Color.LightGray
                )
            }
        }
    }
}

fun formatearTiempo(timestamp: Long): String {
    if (timestamp == 0L) return ""
    val ahora = org.example.abigail.obtenerMilisegundosActuales()
    val diff = ahora - timestamp
    val minutos = diff / 60000
    val horas = minutos / 60
    val dias = horas / 24

    return when {
        minutos < 1 -> "Ahora"
        minutos < 60 -> "Hace ${minutos}min"
        horas < 24 -> "Hace ${horas}h"
        dias < 7 -> "Hace ${dias}d"
        else -> "Hace más de 1 semana"
    }
}